import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Button, Container } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import EmailIcon from '@mui/icons-material/Email';
import ComputerIcon from '@mui/icons-material/Computer';
import HelpIcon from '@mui/icons-material/Help';
import ChatIcon from '@mui/icons-material/Chat';
import BarChart from '@mui/icons-material/BarChart';
import supabase from '../supabaseClient';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import Loader from '../components/Loader';
import { Link } from 'react-router-dom';
import { ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import {
  Search as SearchIcon,
  Store as ShopIcon,
  Assessment as ReportIcon,
  History as HistoryIcon,
  Security as SecurityIcon,
  Memory as AIChipIcon,
  Cloud as CloudIcon,
  ShoppingCart as CartIcon,
  SmartToy as ChatbotIcon,
  Videocam as VideoIcon,
  People as CommunityIcon,
  Storage as PartsIcon,
  Support as SupportIcon
} from '@mui/icons-material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import favicon from '../assets/favicon.png';
import dashboard1 from '../assets/dashboard1.png';
import dashboard2 from '../assets/dashboard2.png';
import dashboard3 from '../assets/dashboard3.png';
import dashboard4 from '../assets/dashboard4.png';

const Dashboard = () => {
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Initial fetch of user profile
    fetchUserProfile();

    // Set up real-time subscription
    const channel = supabase
      .channel('public:profiles')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
        },
        (payload) => {
          console.log('Profile change received:', payload);
          if (payload.new) {
            setUserProfile(payload.new);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchUserProfile = async () => {
    setLoading(true); // Start loading
    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError) {
        throw authError;
      }

      if (user) {
        // Fetch full profile data
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('id, full_name, username')  // Make sure to select full_name
          .eq('id', user.id)
          .single();

        if (profileError) {
          throw profileError;
        }

        console.log('Fetched profile:', profile); // Debug log
        setUserProfile(profile);

        // If profile doesn't exist or needs update, update it
        if (!profile || !profile.full_name) {
          const updates = {
            id: user.id,
            full_name: 'Belhachemia Mohammed', // Default full name
            username: user.email.split('@')[0],
            updated_at: new Date().toISOString(),
          };

          const { error: updateError } = await supabase
            .from('profiles')
            .upsert(updates)
            .eq('id', user.id);

          if (updateError) {
            throw updateError;
          }
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error.message);
    } finally {
      setLoading(false); // End loading
    }
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error.message);
    }
  };

  const handleManageAccount = () => {
    navigate('/profile');
  };

  const menuItems = [
    {
      image: dashboard1,
      title: "AI Diagnosis",
      description: "Advanced fault detection powered by artificial intelligence",
      buttonText: "DETECT NOW",
      link: '/ai-diagnosis'
    },
    {
      image: dashboard2,
      title: "Service History",
      description: "Cloud-based maintenance and service tracking",
      buttonText: "VIEW HISTORY",
      link: '/service-history'
    },
    {
      image: dashboard3,
      title: "Parts Marketplace",
      description: "Genuine spare parts and components",
      buttonText: "SHOP NOW",
      link: '/marketplace'
    },
    {
      image: dashboard4,
      title: "AI Support",
      description: "Intelligent chatbot and customer assistance",
      buttonText: "GET HELP →",
      link: '/ai-support'
    },
    {
      image: dashboard1,
      title: "Expert Consultation",
      description: "Live video consultations with specialists",
      buttonText: "BOOK NOW →",
      link: '/consultation'
    },
    {
      image: dashboard2,
      title: "Secure Payments",
      description: "Protected transactions and fraud prevention",
      buttonText: "MANAGE →",
      link: '/payments'
    },
    {
      image: dashboard3,
      title: "Community Hub",
      description: "Collaborative knowledge sharing platform",
      buttonText: "JOIN NOW →",
      link: '/community'
    }
  ];

  const supportItems = [
    {
      image: dashboard4,
      title: "FAQ & Knowledge Base",
      description: "Find answers to common questions and detailed documentation.",
      buttonText: "LEARN MORE →",
      link: '/faq'
    },
    {
      image: dashboard1,
      title: "Live Chat Support",
      description: "Connect with our support team in real-time.",
      buttonText: "START CHAT →",
      link: '/chat'
    },
    {
      image: dashboard2,
      title: "Email Support",
      description: "Submit detailed inquiries through our email system.",
      buttonText: "CONTACT US →",
      link: '/contact'
    }
  ];

  const navigationLinks = [
    {
      name: 'AI DETECT',
      path: '/ai-detect',
      icon: <SecurityIcon />
    },
    {
      name: 'SHOP',
      path: '/marketplace',
      icon: <ShopIcon />
    },
    {
      name: 'REPORT',
      path: '/report',
      icon: <ReportIcon />
    },
    {
      name: 'SCANS HISTORY',
      path: '/scan-history',
      icon: <HistoryIcon />
    }
  ];

  const navLinks = [
    { text: 'MANAGE ACCOUNT', path: '/profile' },
    { text: 'ASK QUESTION', path: '/ask' },
    { text: 'FEED', path: '/feed' },
    { text: 'ORDERS', path: '/orders' },
    { text: 'SUPPORT', path: '/support' },
    { text: 'REMOTE SUPPORT', path: '/remote-support' },
    { text: 'DOCUMENTATION', path: '/docs' },
    { text: 'SETTINGS', path: '/settings' }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: '#333',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif'
    }}>
      {loading ? (
        <Loader />
      ) : (
        <>
          {/* Welcome Section */}
          <Box sx={{ 
            bgcolor: '#000', 
            color: 'white',
            pt: 4,
            pb: 2,
            position: 'relative'
          }}>
            <Container maxWidth="lg">
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: 1,
                position: 'relative'
              }}>
                <Box
                  component="img"
                  src={favicon}
                  sx={{
                    position: 'absolute',
                    right: '15%',
                    top: '20px',
                    width: '40px',
                    height: '40px',
                    filter: 'brightness(0) invert(1)',
                    animation: 'pulse 2s infinite ease-in-out',
                    '@keyframes pulse': {
                      '0%': {
                        transform: 'scale(1)',
                      },
                      '50%': {
                        transform: 'scale(1.05)',
                      },
                      '100%': {
                        transform: 'scale(1)',
                      },
                    },
                  }}
                />
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    fontWeight: 400,
                    letterSpacing: '0px',
                    mb: 0.5
                  }}
                >
                  Welcome back,
                </Typography>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    fontWeight: 500,
                    letterSpacing: '0px',
                    color: '#00693C',
                    textTransform: 'uppercase'
                  }}
                >
                  {userProfile?.full_name || userProfile?.username || 'USER'}
                </Typography>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    color: 'rgba(255, 255, 255, 0.7)',
                    mt: 1,
                    fontWeight: 400,
                    letterSpacing: '0px'
                  }}
                >
                  Access your dashboard to manage your devices and services
                </Typography>
              </Box>

              {/* Navigation Links */}
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap',
                gap: 3,
                mt: 4,
                pb: 2,
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                {navLinks.map((link, index) => (
                  <Button
                    key={index}
                    component={Link}
                    to={link.path}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.875rem',
                      letterSpacing: '0px',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      fontWeight: 400,
                      padding: '4px 0',
                      borderBottom: '2px solid transparent',
                      borderRadius: 0,
                      transition: 'all 0.2s ease',
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        color: '#fff',
                        borderBottom: '2px solid #00693C',
                      }
                    }}
                  >
                    {link.text}
                  </Button>
                ))}
                
                <Button 
                  onClick={() => supabase.auth.signOut()}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '0.875rem',
                    letterSpacing: '0px',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    fontWeight: 400,
                    padding: '4px 0',
                    borderBottom: '2px solid transparent',
                    borderRadius: 0,
                    marginLeft: 'auto',
                    transition: 'all 0.2s ease',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: '#fff',
                      borderBottom: '2px solid #00693C',
                    }
                  }}
                >
                  Log out
                </Button>
              </Box>
            </Container>
          </Box>

          {/* Main Services Grid */}
          <Container maxWidth="lg" sx={{ py: 6 }}>
            <Grid container spacing={3}>
              {menuItems.map((item, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box
                    sx={{
                      height: '100%',
                      bgcolor: '#fff',
                      borderRadius: 1,
                      p: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      textAlign: 'left',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      transition: 'transform 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                      }
                    }}
                  >
                    <Box
                      component="img"
                      src={item.image}
                      alt={item.title}
                      sx={{
                        width: '40px',
                        height: '40px',
                        objectFit: 'contain'
                      }}
                    />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: '#000', 
                        mt: 2, 
                        mb: 1,
                        fontWeight: 500
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography 
                      sx={{ 
                        color: '#666', 
                        mb: 3, 
                        flex: 1,
                        fontSize: '0.9rem',
                        lineHeight: 1.5
                      }}
                    >
                      {item.description}
                    </Typography>
                    <Button
                      variant="text"
                      onClick={() => navigate(item.link)}
                      sx={{
                        color: '#00693C',
                        textTransform: 'none',
                        fontWeight: 500,
                        p: 0,
                        '&:hover': {
                          bgcolor: 'transparent',
                          color: '#004d2d',
                          '& .MuiSvgIcon-root': {
                            transform: 'translateX(4px)',
                          }
                        }
                      }}
                      endIcon={
                        <ArrowForwardIcon 
                          sx={{ 
                            transition: 'transform 0.2s',
                            color: '#00693C'
                          }} 
                        />
                      }
                    >
                      {item.buttonText}
                    </Button>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>

          {/* Support Section */}
          <Box sx={{ bgcolor: '#333', py: 6 }}>
            <Container maxWidth="lg">
              <Typography variant="h4" sx={{ color: 'white', mb: 4, textAlign: 'center' }}>
                Support & Resources
              </Typography>
              <Grid container spacing={4}>
                {supportItems.map((item, index) => (
                  <Grid item xs={12} md={4} key={index}>
                    <Box
                      sx={{
                        height: '100%',
                        bgcolor: '#fff',
                        borderRadius: 1,
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        textAlign: 'left',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        transition: 'transform 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                        }
                      }}
                    >
                      <Box
                        component="img"
                        src={item.image}
                        alt={item.title}
                        sx={{
                          width: '40px',
                          height: '40px',
                          objectFit: 'contain'
                        }}
                      />
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          color: '#000', 
                          mt: 2, 
                          mb: 1,
                          fontWeight: 500
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography 
                        sx={{ 
                          color: '#666', 
                          mb: 3, 
                          flex: 1,
                          fontSize: '0.9rem',
                          lineHeight: 1.5
                        }}
                      >
                        {item.description}
                      </Typography>
                      <Button
                        variant="text"
                        onClick={() => navigate(item.link)}
                        sx={{
                          color: '#00693C',
                          textTransform: 'none',
                          fontWeight: 500,
                          p: 0,
                          '&:hover': {
                            bgcolor: 'transparent',
                            color: '#004d2d',
                            '& .MuiSvgIcon-root': {
                              transform: 'translateX(4px)',
                            }
                          }
                        }}
                        endIcon={
                          <ArrowForwardIcon 
                            sx={{ 
                              transition: 'transform 0.2s',
                              color: '#00693C'
                            }} 
                          />
                        }
                      >
                        {item.buttonText}
                      </Button>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>
        </>
      )}
    </Box>
  );
};

export default Dashboard;
